import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { DatePipe, CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { GoogleMapsModule } from '@angular/google-maps';
import { RouterModule } from '@angular/router';
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { AppRoutingModule } from './app-routing.module';
// NG Translate
import { TranslateModule } from '@ngx-translate/core';

import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MsalModule,
  MsalService,
  MSAL_INTERCEPTOR_CONFIG,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MsalGuard,
} from '@azure/msal-angular';
import { HttpInterceptorService } from './core/interceptors/http-interceptor.service';

import { environment } from '../environments/environment';
import { DialogTemplateComponent } from '@anotationComponent/dialog-template/dialog-template.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatRadioModule } from '@angular/material/radio';
import { LoaderService } from '@core-services/loader.service';
import { AuthGuardService } from '@core/services/auth-guard.service';
import { AuthService } from '@core/services/adal.service';
import { BnNgIdleService } from 'bn-ng-idle';
import {
  PublicClientApplication,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
} from '@azure/msal-browser';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

const AppAuraPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#fcf3f3',
      100: '#f8e1e1',
      200: '#f0c2c3',
      300: '#e59a9c',
      400: '#d76a6d',
      500: '#ac3234',
      600: '#97292b',
      700: '#7d2022',
      800: '#671b1d',
      900: '#56181a',
      950: '#300b0c',
    },
  },
});

export function MSALInstanceFactory(): IPublicClientApplication {
  const authConfig =
    environment.msalConfiguration?.auth || environment.authConfig?.config?.auth;
  const cacheConfig = environment.authConfig?.config?.cache;

  return new PublicClientApplication({
    auth: {
      clientId: authConfig?.clientId,
      authority: authConfig?.authority,
      redirectUri: authConfig?.redirectUri,
      postLogoutRedirectUri: authConfig?.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: cacheConfig?.cacheLocation,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) {
            return;
          }
          if (level === LogLevel.Error || level === LogLevel.Warning) {
            console.error('[MSAL]', message);
          }
        },
      },
    },
  });
}

export function msalInitializerFactory(
  msalInstance: IPublicClientApplication
): () => Promise<void> {
  return () => msalInstance.initialize();
}

export function MSALGuardConfigFactory() {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.msalLoginReq.scopes,
    },
  };
}

export function MSALInterceptorConfigFactory() {
  const protectedResourceMap = new Map<string, string[]>();

  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'user.read',
  ]);

  protectedResourceMap.set(
    environment.entryAPI,
    environment.msalTokenReq.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

@NgModule({
  declarations: [AppComponent, DialogTemplateComponent],
  exports: [RouterModule],
  bootstrap: [AppComponent],
  imports: [
    GoogleMapsModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    CoreModule,
    SharedModule,
    CommonModule,
    AppRoutingModule,
    MsalModule,
    MatDialogModule,
    MatSnackBarModule,
    MatRadioModule,
    TranslateModule.forRoot({}),
  ],
  providers: [
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: msalInitializerFactory,
      deps: [MSAL_INSTANCE],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
    DatePipe,
    MsalService,
    MsalGuard,
    BnNgIdleService,
    LoaderService,
    AuthGuardService,
    AuthService,
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: AppAuraPreset,
        options: {
          darkModeSelector: false,
        },
      },
    }),
  ],
})
export class AppModule {}
